/**
 * Rug Check: red flags for a token, and a measured track record of how well
 * those flags predict a rug. Protocol: docs/BREAKOUT_WATCH_PLAN.md, Phase 3
 * (pre-registered before this code). Pure logic: no network, no storage.
 *
 * The report never says a token is safe. "No flags" means none of these
 * checks fired; the track record shows how often rugs got past them.
 */
import type { EvmSecurity, Holder, SolanaSecurity } from './goplus';
import type { DexPair } from './dexscreener';

const HOUR_MS = 3_600_000;
const DAY_MS = 24 * HOUR_MS;

export const RUG_CHAINS = {
  ethereum: { label: 'Ethereum', goplus: '1', dex: 'ethereum', evm: true },
  bsc: { label: 'BNB Chain', goplus: '56', dex: 'bsc', evm: true },
  base: { label: 'Base', goplus: '8453', dex: 'base', evm: true },
  arbitrum: { label: 'Arbitrum', goplus: '42161', dex: 'arbitrum', evm: true },
  polygon: { label: 'Polygon', goplus: '137', dex: 'polygon', evm: true },
  solana: { label: 'Solana', goplus: 'solana', dex: 'solana', evm: false },
} as const;

export type RugChain = keyof typeof RUG_CHAINS;

export const isRugChain = (x: unknown): x is RugChain =>
  typeof x === 'string' && Object.hasOwn(RUG_CHAINS, x);

const EVM_ADDRESS = /^0x[0-9a-fA-F]{40}$/;
const SOLANA_ADDRESS = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;

/** The canonical form of a token address, or null if it can't be one on this chain. */
export function normalizeAddress(chain: RugChain, raw: string): string | null {
  const a = raw.trim();
  if (RUG_CHAINS[chain].evm) return EVM_ADDRESS.test(a) ? a.toLowerCase() : null;
  return SOLANA_ADDRESS.test(a) ? a : null;
}

/** A best guess at the chain from an address's format, for the form's default. */
export function guessChain(raw: string): RugChain | null {
  const a = raw.trim();
  if (EVM_ADDRESS.test(a)) return 'ethereum';
  if (SOLANA_ADDRESS.test(a)) return 'solana';
  return null;
}

/** Fixed at registration (Phase 3 protocol). */
export const RUG_RULES = {
  registeredAtMs: Date.parse('2026-10-16T00:00:00Z'),
  resolveAfterMs: 30 * DAY_MS,
  /** A record still unresolved this long after it was due is given up on. */
  giveUpAfterMs: 7 * DAY_MS,
  minTrackedLiquidityUsd: 1_000,
  rugLiquidityRatio: 0.1,
  rugPriceRatio: 0.1,
  verdict: { minResolved: 30, minRugs: 5, minRecall: 0.75, minLift: 2 },
  thresholds: {
    criticalTax: 0.5,
    warningTax: 0.1,
    insiderPercent: 0.05,
    top10Percent: 0.3,
    lpLockedShare: 0.9,
    thinLiquidityUsd: 10_000,
    newPairMs: DAY_MS,
    liquidityToFdv: 0.02,
    volumeToLiquidity: 10,
    solanaTransferFee: 0.1,
  },
} as const;

export type Severity = 'critical' | 'warning';

export interface RugFlag {
  id: string;
  severity: Severity;
  /** Plain-English, may include the measured value. */
  label: string;
}

export interface MarketSnapshot {
  dexId: string;
  pairAddress: string;
  quoteSymbol: string | null;
  priceUsd: number | null;
  liquidityUsd: number | null;
  fdv: number | null;
  volume24h: number | null;
  createdMs: number | null;
}

export interface RugReport {
  chain: RugChain;
  address: string;
  name: string | null;
  symbol: string | null;
  /** Critical first. */
  flags: RugFlag[];
  /** Checks that couldn't be run, in words. */
  unchecked: string[];
  /** The deepest pair, or null when DEX Screener tracks none. */
  market: MarketSnapshot | null;
  /** GoPlus lists it as a well-known token. Shown as a note; flags still stand. */
  recognized: boolean;
  /** False when GoPlus couldn't be reached or had nothing on the token. */
  contractChecked: boolean;
  checkedAtMs: number;
}

const pct = (x: number, digits = 0) => `${(x * 100).toFixed(digits)}%`;
const usd = (x: number) =>
  x >= 1_000_000
    ? `$${(x / 1_000_000).toFixed(1)}M`
    : x >= 1_000
      ? `$${(x / 1_000).toFixed(1)}k`
      : `$${x.toFixed(0)}`;

const BURN_ADDRESSES = new Set([
  '0x000000000000000000000000000000000000dead',
  '0x0000000000000000000000000000000000000000',
]);
const isBurn = (h: Holder) =>
  BURN_ADDRESSES.has(h.address.toLowerCase()) || /burn|dead|null address/i.test(h.tag);

class Collector {
  flags: RugFlag[] = [];
  unchecked: string[] = [];
  flag(id: string, severity: Severity, label: string) {
    this.flags.push({ id, severity, label });
  }
  /** Raise the flag when true, note it as unchecked when unknown. */
  check(
    value: boolean | null,
    id: string,
    severity: Severity,
    label: string,
    what: string,
  ) {
    if (value === true) this.flag(id, severity, label);
    else if (value === null) this.unchecked.push(what);
  }
}

function taxFlags(c: Collector, side: 'buy' | 'sell', tax: number | null) {
  const t = RUG_RULES.thresholds;
  if (tax === null) c.unchecked.push(`${side} tax`);
  else if (tax >= t.criticalTax)
    c.flag(
      `${side}_tax_extreme`,
      'critical',
      `${side === 'buy' ? 'Buy' : 'Sell'} tax ${pct(tax)}`,
    );
  else if (tax >= t.warningTax)
    c.flag(
      `${side}_tax_high`,
      'warning',
      `${side === 'buy' ? 'Buy' : 'Sell'} tax ${pct(tax)}`,
    );
}

function concentration(
  c: Collector,
  holders: Holder[] | null,
  exclude: (h: Holder) => boolean,
) {
  if (!holders) {
    c.unchecked.push('holder concentration');
    return;
  }
  const share = holders.filter((h) => !exclude(h)).reduce((a, h) => a + h.percent, 0);
  if (share > RUG_RULES.thresholds.top10Percent) {
    c.flag('holders_concentrated', 'warning', `Top wallets hold ${pct(share)} of supply`);
  }
}

function lpLock(c: Collector, lp: Holder[] | null) {
  if (!lp) {
    c.unchecked.push('liquidity lock');
    return;
  }
  const locked = lp
    .filter((h) => h.locked || isBurn(h))
    .reduce((a, h) => a + h.percent, 0);
  if (locked < RUG_RULES.thresholds.lpLockedShare) {
    c.flag(
      'lp_unlocked',
      'warning',
      `Only ${pct(Math.min(1, locked))} of liquidity is locked or burned`,
    );
  }
}

export function evmFlags(s: EvmSecurity): Collector {
  const c = new Collector();
  const t = RUG_RULES.thresholds;
  c.check(
    s.honeypot,
    'honeypot',
    'critical',
    'Honeypot: buys work, sells may fail',
    'honeypot test',
  );
  c.check(
    s.cannotSellAll,
    'cannot_sell_all',
    'critical',
    "Holders can't sell their whole balance",
    'sell-all test',
  );
  taxFlags(c, 'buy', s.buyTax);
  taxFlags(c, 'sell', s.sellTax);
  c.check(
    s.ownerChangeBalance,
    'owner_change_balance',
    'critical',
    'Owner can change wallet balances',
    'balance control',
  );
  c.check(s.hiddenOwner, 'hidden_owner', 'critical', 'Hidden owner', 'hidden owner');
  c.check(
    s.selfdestruct,
    'selfdestruct',
    'critical',
    'Contract can self-destruct',
    'self-destruct',
  );
  if (s.openSource === false)
    c.flag('closed_source', 'critical', "Source code isn't verified");
  else if (s.openSource === null) c.unchecked.push('source verification');
  if (s.fakeToken) c.flag('fake_token', 'critical', 'Imitates another token');
  if (s.airdropScam) c.flag('airdrop_scam', 'critical', 'Known airdrop scam');

  const taxEditable =
    s.taxModifiable === true || s.personalTaxModifiable === true
      ? true
      : s.taxModifiable === null && s.personalTaxModifiable === null
        ? null
        : false;
  c.check(
    taxEditable,
    'tax_modifiable',
    'warning',
    'Owner can change the taxes',
    'tax controls',
  );
  c.check(
    s.canTakeBackOwnership,
    'reclaim_ownership',
    'warning',
    'Ownership can be reclaimed',
    'ownership reclaim',
  );
  c.check(
    s.mintable,
    'mintable',
    'warning',
    s.ownerAddress === ''
      ? 'Mint function exists (owner renounced)'
      : 'Owner can mint more tokens',
    'mint function',
  );
  c.check(
    s.transferPausable,
    'pausable',
    'warning',
    'Trading can be paused',
    'pause switch',
  );
  c.check(s.blacklist, 'blacklist', 'warning', 'Wallets can be blacklisted', 'blacklist');
  c.check(
    s.proxy,
    'proxy',
    'warning',
    'Upgradeable proxy: the code can be swapped',
    'proxy',
  );
  c.check(
    s.tradingCooldown,
    'cooldown',
    'warning',
    'Trading cooldown between trades',
    'cooldown',
  );
  const insider = Math.max(s.ownerPercent ?? 0, s.creatorPercent ?? 0);
  if (insider > t.insiderPercent) {
    c.flag('insider_holds', 'warning', `Creator or owner holds ${pct(insider, 1)}`);
  }
  const pools = new Set(s.dexPairs);
  concentration(
    c,
    s.holders,
    (h) => h.locked || isBurn(h) || pools.has(h.address.toLowerCase()),
  );
  lpLock(c, s.lpHolders);
  return c;
}

export function solanaFlags(s: SolanaSecurity): Collector {
  const c = new Collector();
  c.check(
    s.freezable,
    'freezable',
    'critical',
    'Freeze authority is live: your tokens can be frozen',
    'freeze authority',
  );
  c.check(
    s.balanceMutable,
    'balance_mutable',
    'critical',
    'Balances can be changed by an authority',
    'balance authority',
  );
  c.check(
    s.nonTransferable,
    'non_transferable',
    'critical',
    "Token can't be transferred",
    'transferability',
  );
  c.check(
    s.defaultFrozen,
    'default_frozen',
    'critical',
    'New accounts start frozen',
    'default account state',
  );
  if (s.maliciousAddress)
    c.flag(
      'malicious_address',
      'critical',
      'Creator or an authority is a known malicious address',
    );
  c.check(
    s.mintable,
    'mintable',
    'warning',
    'Mint authority is live: more can be printed',
    'mint authority',
  );
  c.check(
    s.closable,
    'closable',
    'warning',
    'Token can be closed by an authority',
    'close authority',
  );
  c.check(
    s.transferHook,
    'transfer_hook',
    'warning',
    'Transfer hook can block or tax transfers',
    'transfer hook',
  );
  if (s.transferFee === null) c.unchecked.push('transfer fee');
  else if (s.transferFee >= RUG_RULES.thresholds.solanaTransferFee) {
    c.flag('transfer_fee', 'warning', `Transfer fee ${pct(s.transferFee, 1)}`);
  }
  concentration(c, s.holders, (h) => h.locked || h.tag !== '');
  lpLock(c, s.lpHolders);
  return c;
}

/** The pair with the most liquidity where this token is the base side. */
export function mainPair(
  pairs: readonly DexPair[],
  chain: RugChain,
  address: string,
): DexPair | null {
  const same = (a: string) =>
    RUG_CHAINS[chain].evm ? a.toLowerCase() === address : a === address;
  const own = pairs.filter(
    (p) => p.chainId === RUG_CHAINS[chain].dex && same(p.baseAddress),
  );
  return own.sort((a, b) => (b.liquidityUsd ?? -1) - (a.liquidityUsd ?? -1))[0] ?? null;
}

export function marketFlags(c: Collector, pair: DexPair | null, nowMs: number) {
  const t = RUG_RULES.thresholds;
  if (!pair) {
    c.flag('no_pair', 'warning', 'No DEX pair found: you may not be able to sell');
    return;
  }
  const liq = pair.liquidityUsd;
  if (liq === null) c.unchecked.push('liquidity');
  else if (liq < t.thinLiquidityUsd)
    c.flag('thin_liquidity', 'warning', `Thin liquidity: ${usd(liq)}`);
  if (pair.createdMs === null) c.unchecked.push('pair age');
  else if (nowMs - pair.createdMs < t.newPairMs) {
    const hours = Math.max(0, Math.floor((nowMs - pair.createdMs) / HOUR_MS));
    c.flag('new_pair', 'warning', `Pair is ${hours} hour${hours === 1 ? '' : 's'} old`);
  }
  if (
    liq !== null &&
    pair.fdv !== null &&
    pair.fdv > 0 &&
    liq / pair.fdv < t.liquidityToFdv
  ) {
    c.flag(
      'liquidity_vs_fdv',
      'warning',
      `Liquidity is ${pct(liq / pair.fdv, 1)} of valuation`,
    );
  }
  if (
    liq !== null &&
    liq > 0 &&
    pair.volume24h !== null &&
    pair.volume24h > t.volumeToLiquidity * liq
  ) {
    c.flag(
      'volume_vs_liquidity',
      'warning',
      `24h volume is ${(pair.volume24h / liq).toFixed(0)}× liquidity (possible wash trading)`,
    );
  }
}

export function buildReport(
  chain: RugChain,
  address: string,
  security: EvmSecurity | SolanaSecurity | null,
  pairs: readonly DexPair[] | null,
  nowMs: number,
): RugReport {
  let c: Collector;
  if (security === null) {
    c = new Collector();
    c.unchecked.push(
      'contract checks (GoPlus had nothing on this token or was unreachable)',
    );
  } else {
    c = RUG_CHAINS[chain].evm
      ? evmFlags(security as EvmSecurity)
      : solanaFlags(security as SolanaSecurity);
  }
  const pair = pairs === null ? null : mainPair(pairs, chain, address);
  if (pairs === null) c.unchecked.push('market checks (DEX Screener was unreachable)');
  else marketFlags(c, pair, nowMs);

  const order: Record<Severity, number> = { critical: 0, warning: 1 };
  return {
    chain,
    address,
    name: security?.name ?? pair?.baseName ?? null,
    symbol: security?.symbol ?? pair?.baseSymbol ?? null,
    flags: c.flags.sort((a, b) => order[a.severity] - order[b.severity]),
    unchecked: c.unchecked,
    market: pair
      ? {
          dexId: pair.dexId,
          pairAddress: pair.pairAddress,
          quoteSymbol: pair.quoteSymbol,
          priceUsd: pair.priceUsd,
          liquidityUsd: pair.liquidityUsd,
          fdv: pair.fdv,
          volume24h: pair.volume24h,
          createdMs: pair.createdMs,
        }
      : null,
    recognized: security?.recognized ?? false,
    contractChecked: security !== null,
    checkedAtMs: nowMs,
  };
}

// ---- tracking ----

export type RugGroup = 'flagged' | 'warnings' | 'clean';
export type RugOutcome = 'rug' | 'survived';

/** One looked-up token. Stores only derived flag IDs, never GoPlus data. */
export interface TrackedToken {
  chain: RugChain;
  address: string;
  symbol: string | null;
  lookedUpMs: number;
  criticalIds: string[];
  warningIds: string[];
  contractChecked: boolean;
  pairAddress: string;
  liquidityUsd: number;
  priceUsd: number;
  outcome: RugOutcome | null;
  resolvedMs: number | null;
  /** At resolution; null when the pair was gone. */
  liquidityUsdLater: number | null;
  priceUsdLater: number | null;
}

export const groupOf = (t: Pick<TrackedToken, 'criticalIds' | 'warningIds'>): RugGroup =>
  t.criticalIds.length > 0 ? 'flagged' : t.warningIds.length > 0 ? 'warnings' : 'clean';

export const trackedKey = (chain: RugChain, address: string) => `${chain}:${address}`;

/**
 * The record a lookup adds to the track record, or null when it shouldn't
 * count: before registration, no tradable pair, or too little liquidity.
 */
export function trackRecord(report: RugReport): TrackedToken | null {
  const m = report.market;
  if (report.checkedAtMs < RUG_RULES.registeredAtMs) return null;
  if (!m || m.liquidityUsd === null || m.priceUsd === null || m.priceUsd <= 0)
    return null;
  if (m.liquidityUsd < RUG_RULES.minTrackedLiquidityUsd) return null;
  return {
    chain: report.chain,
    address: report.address,
    symbol: report.symbol,
    lookedUpMs: report.checkedAtMs,
    criticalIds: report.flags.filter((f) => f.severity === 'critical').map((f) => f.id),
    warningIds: report.flags.filter((f) => f.severity === 'warning').map((f) => f.id),
    contractChecked: report.contractChecked,
    pairAddress: m.pairAddress,
    liquidityUsd: m.liquidityUsd,
    priceUsd: m.priceUsd,
    outcome: null,
    resolvedMs: null,
    liquidityUsdLater: null,
    priceUsdLater: null,
  };
}

export const isDue = (t: TrackedToken, nowMs: number) =>
  t.outcome === null && nowMs >= t.lookedUpMs + RUG_RULES.resolveAfterMs;

/**
 * Resolves a due record against the token's pairs now: a rug when the same
 * pair's liquidity or price fell below 10% of what it was, or the token has
 * no pairs left. If the original pair is gone but others remain, the deepest
 * remaining pair stands in.
 */
export function resolveOutcome(
  t: TrackedToken,
  pairsNow: readonly DexPair[],
  nowMs: number,
): TrackedToken {
  const own = pairsNow.filter((p) =>
    RUG_CHAINS[t.chain].evm
      ? p.baseAddress.toLowerCase() === t.address
      : p.baseAddress === t.address,
  );
  const pair =
    own.find((p) => p.pairAddress === t.pairAddress) ??
    own.sort((a, b) => (b.liquidityUsd ?? -1) - (a.liquidityUsd ?? -1))[0] ??
    null;
  const liq = pair?.liquidityUsd ?? null;
  const price = pair?.priceUsd ?? null;
  const rug =
    pair === null ||
    (liq !== null && liq < RUG_RULES.rugLiquidityRatio * t.liquidityUsd) ||
    (price !== null && price < RUG_RULES.rugPriceRatio * t.priceUsd);
  return {
    ...t,
    outcome: rug ? 'rug' : 'survived',
    resolvedMs: nowMs,
    liquidityUsdLater: liq,
    priceUsdLater: price,
  };
}

// ---- parsing ----

const isNum = (x: unknown): x is number => typeof x === 'number' && Number.isFinite(x);
const strArr = (x: unknown) =>
  Array.isArray(x) ? x.filter((v): v is string => typeof v === 'string') : [];

export function parseTracked(raw: unknown): TrackedToken | null {
  const t = raw as Partial<TrackedToken> | null;
  if (!t || !isRugChain(t.chain) || typeof t.address !== 'string') return null;
  if (normalizeAddress(t.chain, t.address) !== t.address) return null;
  if (!isNum(t.lookedUpMs) || typeof t.pairAddress !== 'string') return null;
  if (!isNum(t.liquidityUsd) || !isNum(t.priceUsd)) return null;
  const outcome = t.outcome === 'rug' || t.outcome === 'survived' ? t.outcome : null;
  return {
    chain: t.chain,
    address: t.address,
    symbol: typeof t.symbol === 'string' ? t.symbol.slice(0, 16) : null,
    lookedUpMs: t.lookedUpMs,
    criticalIds: strArr(t.criticalIds),
    warningIds: strArr(t.warningIds),
    contractChecked: t.contractChecked !== false,
    pairAddress: t.pairAddress,
    liquidityUsd: t.liquidityUsd,
    priceUsd: t.priceUsd,
    outcome,
    resolvedMs: outcome && isNum(t.resolvedMs) ? t.resolvedMs : null,
    liquidityUsdLater: isNum(t.liquidityUsdLater) ? t.liquidityUsdLater : null,
    priceUsdLater: isNum(t.priceUsdLater) ? t.priceUsdLater : null,
  };
}

export interface RugTrack {
  schemaVersion: 1;
  /** Keyed by trackedKey(). */
  tokens: Record<string, TrackedToken>;
  lastRunMs: number | null;
  lastFailures: string[];
}

export const emptyRugTrack = (): RugTrack => ({
  schemaVersion: 1,
  tokens: {},
  lastRunMs: null,
  lastFailures: [],
});

export function parseRugTrack(raw: string): RugTrack {
  try {
    const p = JSON.parse(raw) as Partial<RugTrack>;
    if (p?.schemaVersion !== 1) return emptyRugTrack();
    const tokens: Record<string, TrackedToken> = {};
    for (const v of Object.values(p.tokens ?? {})) {
      const t = parseTracked(v);
      if (t) tokens[trackedKey(t.chain, t.address)] = t;
    }
    return {
      schemaVersion: 1,
      tokens,
      lastRunMs: isNum(p.lastRunMs) ? p.lastRunMs : null,
      lastFailures: strArr(p.lastFailures),
    };
  } catch {
    return emptyRugTrack();
  }
}

// ---- summary ----

export type RugVerdict = 'collecting' | 'useful' | 'weak';

export interface GroupStats {
  resolved: number;
  rugs: number;
  rugRate: number | null;
}

export interface RugTrackSummary {
  tracked: number;
  pending: number;
  resolved: number;
  rugs: number;
  groups: Record<RugGroup, GroupStats>;
  /** Share of rugs that had a critical flag. */
  recall: number | null;
  /** Share of rugs that had no flags at all. */
  missRate: number | null;
  /** Flagged rug rate ÷ clean rug rate; Infinity when clean never rugged. */
  lift: number | null;
  verdict: RugVerdict;
}

export function summarizeRugTrack(track: RugTrack): RugTrackSummary {
  const all = Object.values(track.tokens);
  const done = all.filter((t) => t.outcome !== null);
  const groups = Object.fromEntries(
    (['flagged', 'warnings', 'clean'] as const).map((g) => {
      const inG = done.filter((t) => groupOf(t) === g);
      const rugs = inG.filter((t) => t.outcome === 'rug').length;
      return [
        g,
        { resolved: inG.length, rugs, rugRate: inG.length ? rugs / inG.length : null },
      ];
    }),
  ) as Record<RugGroup, GroupStats>;
  const rugs = done.filter((t) => t.outcome === 'rug');
  const recall = rugs.length
    ? rugs.filter((t) => groupOf(t) === 'flagged').length / rugs.length
    : null;
  const missRate = rugs.length
    ? rugs.filter((t) => groupOf(t) === 'clean').length / rugs.length
    : null;
  const f = groups.flagged.rugRate;
  const c = groups.clean.rugRate;
  const lift =
    f === null || c === null ? null : c === 0 ? (f > 0 ? Infinity : null) : f / c;

  const v = RUG_RULES.verdict;
  let verdict: RugVerdict = 'collecting';
  if (done.length >= v.minResolved && rugs.length >= v.minRugs) {
    // A clean rug rate of zero (lift Infinity, or no clean tokens rugging)
    // meets the lift bar, per the protocol.
    const liftOk = c === 0 || (lift !== null && lift >= v.minLift);
    verdict = recall !== null && recall >= v.minRecall && liftOk ? 'useful' : 'weak';
  }
  return {
    tracked: all.length,
    pending: all.length - done.length,
    resolved: done.length,
    rugs: rugs.length,
    groups,
    recall,
    missRate,
    lift,
    verdict,
  };
}
